import type { MusicalEmotionProfile } from "@shared/schema";

const BASE_TEMPO = 72;
const MAX_TEMPO_DRIFT = 12;
const A4_HZ = 440;
const MAX_HARMONICS = 8;

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const;

const SCALE_INTERVALS: Record<string, number[]> = {
  ionian:     [0, 2, 4, 5, 7, 9, 11],
  dorian:     [0, 2, 3, 5, 7, 9, 10],
  phrygian:   [0, 1, 3, 5, 7, 8, 10],
  lydian:     [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  aeolian:    [0, 2, 3, 5, 7, 8, 10],
};

const REGISTER_OCTAVE: Record<string, number> = {
  "low": 2,
  "mid-low": 3,
  "mid": 4,
  "mid-high": 5,
  "high": 6,
};

const RHYTHM_GRID_DURATION: Record<string, number> = {
  "whole": 4,
  "half": 2,
  "quarter": 1,
  "eighth": 0.5,
  "sixteenth": 0.25,
};

const DENSITY_NOTE_COUNT: Record<string, number> = {
  "sparse": 2,
  "low": 3,
  "moderate": 4,
  "high": 6,
  "dense": 8,
};

const HOME_MOTIF_DEGREES = [1, 3, 5, 3, 1];

function midiToFreq(midi: number): number {
  return A4_HZ * Math.pow(2, (midi - 69) / 12);
}

function noteNameToMidi(name: string, octave: number): number {
  const idx = NOTE_NAMES.indexOf(name as typeof NOTE_NAMES[number]);
  if (idx === -1) return 60;
  return (octave + 1) * 12 + idx;
}

function scaleDegreeToMidi(
  degree: number,
  mode: string,
  rootNote: string,
  octave: number
): number {
  const intervals = SCALE_INTERVALS[mode] || SCALE_INTERVALS.ionian;
  const rootMidi = noteNameToMidi(rootNote, octave);
  const degreeIdx = ((degree - 1) % 7 + 7) % 7;
  const octaveShift = Math.floor((degree - 1) / 7);
  return rootMidi + intervals[degreeIdx] + octaveShift * 12;
}

function buildOvertones(fundamental: number, weights: number[]): number[] {
  const result: number[] = [];
  for (let h = 0; h < weights.length; h++) {
    const freq = fundamental * (h + 1);
    if (freq <= 8000) result.push(freq);
  }
  return result;
}

function computeSpectralCentroid(frequencies: number[], weights: number[]): number {
  let weightedSum = 0;
  let totalWeight = 0;
  for (let i = 0; i < frequencies.length; i++) {
    const w = i < weights.length ? weights[i] : 0;
    weightedSum += frequencies[i] * w;
    totalWeight += w;
  }
  return totalWeight > 0 ? weightedSum / totalWeight : frequencies[0] || 440;
}

interface TimbreEnvelope {
  attack: number;
  decay: number;
  sustain: number;
  release: number;
  harmonicWeights: number[];
}

const TIMBRE_ENVELOPES: Record<string, TimbreEnvelope> = {
  "warm-pad": {
    attack: 0.3, decay: 0.5, sustain: 0.7, release: 1.0,
    harmonicWeights: [1.0, 0.5, 0.3, 0.15, 0.08],
  },
  "soft-strings": {
    attack: 0.15, decay: 0.4, sustain: 0.6, release: 0.8,
    harmonicWeights: [1.0, 0.7, 0.4, 0.2, 0.1, 0.05],
  },
  "clean-bell": {
    attack: 0.01, decay: 0.8, sustain: 0.1, release: 1.2,
    harmonicWeights: [1.0, 0.3, 0.1, 0.5, 0.08, 0.3],
  },
  "bright-pluck": {
    attack: 0.005, decay: 0.3, sustain: 0.05, release: 0.4,
    harmonicWeights: [1.0, 0.8, 0.6, 0.4, 0.3, 0.2, 0.1],
  },
  "thin-reed": {
    attack: 0.05, decay: 0.2, sustain: 0.5, release: 0.3,
    harmonicWeights: [1.0, 0.2, 0.8, 0.15, 0.5, 0.1],
  },
  "dark-drone": {
    attack: 0.8, decay: 0.3, sustain: 0.9, release: 2.0,
    harmonicWeights: [1.0, 0.8, 0.6, 0.4, 0.2],
  },
  "gentle-harp": {
    attack: 0.01, decay: 0.6, sustain: 0.15, release: 1.5,
    harmonicWeights: [1.0, 0.4, 0.15, 0.06, 0.02],
  },
  "glass-shimmer": {
    attack: 0.02, decay: 1.0, sustain: 0.2, release: 2.0,
    harmonicWeights: [0.3, 0.1, 0.05, 1.0, 0.5, 0.8, 0.3],
  },
};

export interface ResonanceAudioState {
  sessionKey: string;
  baseTempo: number;
  isPlaying: boolean;
  currentEmotion: string | null;
  volume: number;
}

export interface HarmonicAnalysis {
  baseFrequency: number;
  scaleDegree: number;
  key: string;
  mode: string;
  chordFrequencies: number[];
  overtones: number[];
  spectralCentroid: number;
  harmonicDensity: number;
  brightness: number;
  consonanceRatio: number;
  mappedColor: string;
  spatialVector: [number, number, number];
}

const MAJOR_CONSONANT = new Set([0, 4, 7, 12]);
const MINOR_CONSONANT = new Set([0, 3, 7, 12]);
const MAJOR_MODES = new Set(["ionian", "lydian", "mixolydian"]);

function computeConsonanceRatio(midiNotes: number[], mode: string): number {
  const consonantIntervals = MAJOR_MODES.has(mode) ? MAJOR_CONSONANT : MINOR_CONSONANT;
  const intervals = new Set<number>();
  for (let i = 0; i < midiNotes.length; i++) {
    for (let j = i + 1; j < midiNotes.length; j++) {
      intervals.add(Math.abs(midiNotes[i] - midiNotes[j]) % 12);
    }
  }
  if (intervals.size === 0) return 1;
  let consonant = 0;
  intervals.forEach(iv => { if (consonantIntervals.has(iv)) consonant++; });
  return consonant / intervals.size;
}

function deriveColor(scaleDegree: number, consonanceRatio: number, harmonicDensity: number): string {
  const hue = ((scaleDegree - 1) * (360 / 7)) % 360;
  const saturation = Math.round(30 + consonanceRatio * 60);
  const lightness = Math.round(25 + harmonicDensity * 45);
  return `hsl(${Math.round(hue)}, ${saturation}%, ${lightness}%)`;
}

export class ResonanceAudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private activeOscillators: OscillatorNode[] = [];
  private activeGains: GainNode[] = [];
  private sessionKey: string;
  private baseTempo: number;
  private _isPlaying = false;
  private _volume = 0.15;
  private _isPastoralOverride = false;
  private lastHarmonicAnalysis: HarmonicAnalysis | null = null;
  private scheduledTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(sessionKey = "C", baseTempo = BASE_TEMPO) {
    this.sessionKey = sessionKey;
    this.baseTempo = baseTempo;
  }

  async unlock(): Promise<boolean> {
    try {
      if (!this.ctx) {
        this.ctx = new AudioContext();
        this.masterGain = this.ctx.createGain();
        this.masterGain.gain.value = this._volume;
        this.masterGain.connect(this.ctx.destination);
      }
      if (this.ctx.state === "suspended") {
        await this.ctx.resume();
      }
      return this.ctx.state === "running";
    } catch (_) {
      return false;
    }
  }

  get isUnlocked(): boolean {
    return this.ctx?.state === "running";
  }

  get isPlaying(): boolean {
    return this._isPlaying;
  }

  get volume(): number {
    return this._volume;
  }

  set volume(v: number) {
    this._volume = Math.max(0, Math.min(1, v));
    if (this.masterGain) {
      this.masterGain.gain.setTargetAtTime(this._volume, this.ctx!.currentTime, 0.05);
    }
  }

  get harmonicAnalysis(): HarmonicAnalysis | null {
    return this.lastHarmonicAnalysis;
  }

  getState(): ResonanceAudioState {
    return {
      sessionKey: this.sessionKey,
      baseTempo: this.baseTempo,
      isPlaying: this._isPlaying,
      currentEmotion: null,
      volume: this._volume,
    };
  }

  stop(): void {
    if (this.scheduledTimeout) {
      clearTimeout(this.scheduledTimeout);
      this.scheduledTimeout = null;
    }
    const now = this.ctx?.currentTime ?? 0;
    for (const gain of this.activeGains) {
      try { gain.gain.setTargetAtTime(0, now, 0.1); } catch (_) { /* audio node already disconnected */ }
    }
    setTimeout(() => {
      for (const osc of this.activeOscillators) {
        try { osc.stop(); osc.disconnect(); } catch (_) { /* oscillator already stopped */ }
      }
      for (const gain of this.activeGains) {
        try { gain.disconnect(); } catch (_) { /* gain node already disconnected */ }
      }
      this.activeOscillators = [];
      this.activeGains = [];
    }, 300);
    this._isPlaying = false;
    this._isPastoralOverride = false;
  }

  playEmotionResponse(
    profile: MusicalEmotionProfile,
    pastoralMode: string = "normal"
  ): HarmonicAnalysis | null {
    if (!this.ctx || !this.masterGain) return null;

    this.stop();
    this._isPlaying = true;

    const isPastoralOverride = pastoralMode === "warfare_soften" || pastoralMode === "grounding";
    this._isPastoralOverride = isPastoralOverride;

    let effectiveProfile = { ...profile };
    if (isPastoralOverride) {
      effectiveProfile = {
        ...profile,
        mode: "ionian" as const,
        chordPool: ["I", "V"],
        rhythmGrid: "whole" as const,
        noteDensity: "sparse" as const,
        tempoModifier: -15,
        register: "mid-low" as const,
        timbre: "warm-pad" as const,
        harmonicBrightness: 0.4,
      };
    }

    const tempo = Math.max(
      this.baseTempo - MAX_TEMPO_DRIFT,
      Math.min(this.baseTempo + MAX_TEMPO_DRIFT, this.baseTempo + effectiveProfile.tempoModifier)
    );
    const beatDuration = 60 / tempo;
    const gridDuration = beatDuration * RHYTHM_GRID_DURATION[effectiveProfile.rhythmGrid];
    const noteCount = DENSITY_NOTE_COUNT[effectiveProfile.noteDensity];
    const octave = REGISTER_OCTAVE[effectiveProfile.register];
    const timbreEnv = TIMBRE_ENVELOPES[effectiveProfile.timbre] || TIMBRE_ENVELOPES["warm-pad"];

    const primaryScaleDegree = effectiveProfile.scaleDegreeFocus[0] || 1;

    const motifMidis = HOME_MOTIF_DEGREES.map(deg =>
      scaleDegreeToMidi(deg, effectiveProfile.mode, this.sessionKey, octave)
    );

    const chordMidis = effectiveProfile.scaleDegreeFocus.map(deg =>
      scaleDegreeToMidi(deg, effectiveProfile.mode, this.sessionKey, octave)
    );

    const chordFreqs = chordMidis.map(midiToFreq);

    const sequence: number[] = [];
    for (let i = 0; i < noteCount; i++) {
      if (i < motifMidis.length) {
        sequence.push(motifMidis[i]);
      } else {
        sequence.push(chordMidis[i % chordMidis.length]);
      }
    }

    const fundamentalHz = midiToFreq(scaleDegreeToMidi(
      primaryScaleDegree, effectiveProfile.mode, this.sessionKey, octave
    ));

    const allOvertones = buildOvertones(fundamentalHz, timbreEnv.harmonicWeights);

    const spectralCentroid = computeSpectralCentroid(allOvertones, timbreEnv.harmonicWeights);

    const harmonicDensity = allOvertones.length / MAX_HARMONICS;

    const consonanceRatio = computeConsonanceRatio(chordMidis, effectiveProfile.mode);

    const centroidNorm = Math.min(1, Math.max(0, (spectralCentroid - 100) / 3900));
    const brightness = centroidNorm * effectiveProfile.harmonicBrightness;

    const mappedColor = deriveColor(primaryScaleDegree, consonanceRatio, harmonicDensity);

    const spatialVector: [number, number, number] = [
      Math.log2(fundamentalHz / A4_HZ),
      Math.min(1, Math.max(0, spectralCentroid / 8000)),
      harmonicDensity,
    ];

    const now = this.ctx.currentTime + 0.05;

    for (let i = 0; i < sequence.length; i++) {
      const noteStart = now + i * gridDuration;
      const noteDuration = gridDuration * 0.9;
      const freq = midiToFreq(sequence[i]);

      for (let h = 0; h < timbreEnv.harmonicWeights.length; h++) {
        const harmonicFreq = freq * (h + 1);
        const harmonicGain = timbreEnv.harmonicWeights[h] * effectiveProfile.harmonicBrightness;

        if (harmonicFreq > 8000) continue;

        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = "sine";
        osc.frequency.value = harmonicFreq;
        gain.gain.value = 0;

        gain.gain.setValueAtTime(0, noteStart);
        gain.gain.linearRampToValueAtTime(
          harmonicGain * 0.3,
          noteStart + timbreEnv.attack * noteDuration
        );
        gain.gain.linearRampToValueAtTime(
          harmonicGain * timbreEnv.sustain * 0.3,
          noteStart + (timbreEnv.attack + timbreEnv.decay * 0.5) * noteDuration
        );
        gain.gain.setValueAtTime(
          harmonicGain * timbreEnv.sustain * 0.3,
          noteStart + noteDuration - timbreEnv.release * noteDuration * 0.3
        );
        gain.gain.linearRampToValueAtTime(0, noteStart + noteDuration);

        osc.connect(gain);
        gain.connect(this.masterGain!);
        osc.start(noteStart);
        osc.stop(noteStart + noteDuration + 0.1);

        this.activeOscillators.push(osc);
        this.activeGains.push(gain);
      }
    }

    this.lastHarmonicAnalysis = {
      baseFrequency: fundamentalHz,
      scaleDegree: primaryScaleDegree,
      key: this.sessionKey,
      mode: effectiveProfile.mode,
      chordFrequencies: chordFreqs,
      overtones: allOvertones,
      spectralCentroid,
      harmonicDensity,
      brightness,
      consonanceRatio,
      mappedColor,
      spatialVector,
    };

    const totalDuration = sequence.length * gridDuration * 1000 + 500;
    this.scheduledTimeout = setTimeout(() => {
      this._isPlaying = false;
      this._isPastoralOverride = false;
      this.activeOscillators = [];
      this.activeGains = [];
    }, totalDuration);

    return this.lastHarmonicAnalysis;
  }

  destroy(): void {
    this.stop();
    if (this.ctx) {
      try { this.ctx.close(); } catch (_) { /* audio context already closed */ }
      this.ctx = null;
      this.masterGain = null;
    }
  }
}

export function deriveColorFromHarmonics(analysis: HarmonicAnalysis | null, fallbackHex: string): string {
  if (!analysis) return fallbackHex;
  return analysis.mappedColor;
}

let _instance: ResonanceAudioEngine | null = null;

export function getResonanceAudio(): ResonanceAudioEngine {
  if (!_instance) {
    _instance = new ResonanceAudioEngine("C", BASE_TEMPO);
  }
  return _instance;
}
